/**
 * AI Mastering Suggestions Panel
 * Real-time audio analysis and AI-powered mastering recommendations
 * 
 * Features:
 * - Live audio characteristic analysis
 * - AI-generated actionable suggestions
 * - User feedback loop for refinement
 * - Target platform and genre profiles
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Brain,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  Zap,
  Target,
  Music,
  Volume2,
  Settings2,
  ThumbsUp,
  ThumbsDown,
  Star,
  ExternalLink,
  Loader2,
  BarChart3,
  Activity,
  Radio,
  Disc3,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIORITY_ICONS = {
  critical: AlertTriangle,
  high: AlertCircle,
  medium: Info,
  low: CheckCircle,
};

const PRIORITY_COLORS = {
  critical: 'text-red-500 bg-red-500/10 border-red-500/30',
  high: 'text-orange-500 bg-orange-500/10 border-orange-500/30',
  medium: 'text-amber-500 bg-amber-500/10 border-amber-500/30',
  low: 'text-green-500 bg-green-500/10 border-green-500/30',
};

const CATEGORY_LABELS = {
  loudness: { label: 'Loudness', icon: Volume2, color: 'text-violet-400' },
  eq: { label: 'EQ', icon: BarChart3, color: 'text-cyan-400' },
  dynamics: { label: 'Dynamics', icon: Activity, color: 'text-amber-400' },
  stereo: { label: 'Stereo', icon: Radio, color: 'text-emerald-400' },
  limiter: { label: 'Limiter', icon: Zap, color: 'text-rose-400' },
  general: { label: 'General', icon: Settings2, color: 'text-zinc-400' },
};

const PLATFORM_OPTIONS = [
  { value: 'streaming', label: 'Streaming (Spotify, Apple Music)', icon: Music },
  { value: 'youtube', label: 'YouTube', icon: ExternalLink },
  { value: 'cd', label: 'CD / High Fidelity', icon: Disc3 },
  { value: 'club', label: 'Club / DJ Play', icon: Volume2 },
  { value: 'broadcast', label: 'Broadcast (TV/Radio)', icon: Radio },
  { value: 'vinyl', label: 'Vinyl', icon: Disc3 },
];

const GENRE_OPTIONS = [
  'Pop', 'Hip-Hop', 'EDM', 'Rock', 'Metal', 'Jazz', 'Classical', 'Folk', 'Ambient', 'R&B'
];


// ============================================================================
// SUGGESTION CARD COMPONENT
// ============================================================================

const SuggestionCard = ({ 
  suggestion, 
  onApply, 
  onFeedback, 
  isExpanded, 
  onToggle 
}) => {
  const [localRating, setLocalRating] = useState(0);
  const [feedbackSent, setFeedbackSent] = useState(false);
  
  const PriorityIcon = PRIORITY_ICONS[suggestion.priority] || Info;
  const CategoryInfo = CATEGORY_LABELS[suggestion.category] || CATEGORY_LABELS.general;
  const CategoryIcon = CategoryInfo.icon;
  
  const handleFeedback = async (helpful, rating = null) => {
    if (feedbackSent) return;
    
    try {
      await onFeedback(suggestion.id, {
        helpful,
        rating: rating || localRating,
        applied: false,
      });
      setFeedbackSent(true);
      toast.success('Feedback submitted');
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };
  
  const handleApply = () => {
    onApply?.(suggestion);
    onFeedback?.(suggestion.id, { applied: true, helpful: true });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        "border rounded-lg overflow-hidden transition-all",
        PRIORITY_COLORS[suggestion.priority]
      )}
      data-testid={`suggestion-${suggestion.id}`}
    >
      {/* Header */}
      <div 
        className="flex items-start gap-3 p-3 cursor-pointer hover:bg-white/5 transition-colors"
        onClick={onToggle}
      >
        <div className={cn(
          "p-1.5 rounded-lg",
          PRIORITY_COLORS[suggestion.priority].replace('text-', 'bg-').replace('/10', '/20')
        )}>
          <PriorityIcon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <CategoryIcon className={cn("w-3.5 h-3.5", CategoryInfo.color)} />
            <span className={cn("text-[10px] font-medium uppercase", CategoryInfo.color)}>
              {CategoryInfo.label}
            </span>
            <span className={cn(
              "text-[9px] px-1.5 py-0.5 rounded font-medium uppercase",
              suggestion.priority === 'critical' ? 'bg-red-500/30 text-red-300' :
              suggestion.priority === 'high' ? 'bg-orange-500/30 text-orange-300' :
              suggestion.priority === 'medium' ? 'bg-amber-500/30 text-amber-300' :
              'bg-green-500/30 text-green-300'
            )}>
              {suggestion.priority}
            </span>
          </div>
          <h4 className="text-sm font-medium text-white">{suggestion.title}</h4>
          <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">
            {suggestion.description}
          </p>
        </div>
        
        <button className="p-1 text-zinc-400 hover:text-white">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      
      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10"
          >
            <div className="p-3 space-y-3">
              {/* Action */}
              {suggestion.action && (
                <div className="bg-black/30 rounded-lg p-2.5">
                  <span className="text-[10px] text-zinc-500 uppercase font-medium">Action</span>
                  <p className="text-sm text-white mt-0.5">{suggestion.action}</p>
                </div>
              )}
              
              {/* Parameter Suggestion */}
              {(suggestion.parameter || suggestion.suggested_value !== null) && (
                <div className="flex items-center gap-3 bg-black/30 rounded-lg p-2.5">
                  <div className="flex-1">
                    {suggestion.parameter && (
                      <span className="text-[10px] text-zinc-500 block">{suggestion.parameter}</span>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {suggestion.current_value !== null && (
                        <span className="text-sm text-zinc-400">
                          {suggestion.current_value.toFixed(1)}
                        </span>
                      )}
                      {suggestion.current_value !== null && suggestion.suggested_value !== null && (
                        <span className="text-zinc-600">→</span>
                      )}
                      {suggestion.suggested_value !== null && (
                        <span className="text-sm font-medium text-emerald-400">
                          {suggestion.suggested_value.toFixed(1)}
                        </span>
                      )}
                      {suggestion.suggested_range && (
                        <span className="text-xs text-zinc-500">
                          ({suggestion.suggested_range.min.toFixed(1)} to {suggestion.suggested_range.max.toFixed(1)})
                        </span>
                      )}
                    </div>
                  </div>
                  {suggestion.plugin_type && (
                    <span className="text-[10px] px-2 py-1 bg-violet-500/20 text-violet-300 rounded">
                      {suggestion.plugin_type}
                    </span>
                  )}
                </div>
              )}
              
              {/* Reasoning */}
              {suggestion.reasoning && (
                <div className="text-xs text-zinc-400 italic">
                  💡 {suggestion.reasoning}
                </div>
              )}
              
              {/* Actions Row */}
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                {/* Feedback */}
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500">Helpful?</span>
                  <button
                    onClick={() => handleFeedback(true, 5)}
                    disabled={feedbackSent}
                    className={cn(
                      "p-1 rounded hover:bg-emerald-500/20 transition-colors",
                      feedbackSent && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <ThumbsUp className="w-4 h-4 text-emerald-400" />
                  </button>
                  <button
                    onClick={() => handleFeedback(false, 1)}
                    disabled={feedbackSent}
                    className={cn(
                      "p-1 rounded hover:bg-red-500/20 transition-colors",
                      feedbackSent && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <ThumbsDown className="w-4 h-4 text-red-400" />
                  </button>
                </div>
                
                {/* Apply Button */}
                {suggestion.suggested_value !== null && (
                  <Button
                    size="sm"
                    onClick={handleApply}
                    className="h-7 text-xs bg-violet-500 hover:bg-violet-600"
                  >
                    Apply Suggestion
                  </Button>
                )}
              </div>
              
              {/* Confidence */}
              {suggestion.confidence && (
                <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                  <span>AI Confidence:</span>
                  <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-violet-500 to-cyan-500"
                      style={{ width: `${suggestion.confidence * 100}%` }}
                    />
                  </div>
                  <span>{(suggestion.confidence * 100).toFixed(0)}%</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};


// ============================================================================
// SCORE DISPLAY COMPONENT
// ============================================================================

const ScoreDisplay = ({ score, grade }) => {
  const getGradeColor = (g) => {
    switch (g) {
      case 'A': return 'text-emerald-400';
      case 'B': return 'text-cyan-400';
      case 'C': return 'text-amber-400';
      case 'D': return 'text-orange-400';
      default: return 'text-red-400';
    }
  };
  
  const getScoreGradient = (s) => {
    if (s >= 80) return 'from-emerald-500 to-cyan-500';
    if (s >= 60) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };
  
  return (
    <div className="flex items-center gap-4">
      {/* Score Ring */}
      <div className="relative w-20 h-20">
        <svg className="w-full h-full -rotate-90">
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            className="text-zinc-800"
          />
          <circle
            cx="40"
            cy="40"
            r="36"
            fill="none"
            stroke="url(#scoreGradient)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${score * 2.26} 226`}
          />
          <defs>
            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className={`stop-color: ${score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444'}`} />
              <stop offset="100%" className={`stop-color: ${score >= 80 ? '#06b6d4' : score >= 60 ? '#f97316' : '#f43f5e'}`} />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn("text-2xl font-bold", getGradeColor(grade))}>{grade}</span>
          <span className="text-[10px] text-zinc-500">{score}/100</span>
        </div>
      </div>
      
      {/* Score Bars */}
      <div className="flex-1 space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="w-16 text-zinc-400">Loudness</span>
          <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full bg-gradient-to-r", getScoreGradient(score))} 
                 style={{ width: `${Math.min(100, score + 10)}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-16 text-zinc-400">Frequency</span>
          <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full bg-gradient-to-r", getScoreGradient(score))} 
                 style={{ width: `${Math.min(100, score + 5)}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-16 text-zinc-400">Dynamics</span>
          <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full bg-gradient-to-r", getScoreGradient(score))} 
                 style={{ width: `${Math.min(100, score - 5)}%` }} />
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="w-16 text-zinc-400">Stereo</span>
          <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full bg-gradient-to-r", getScoreGradient(score))} 
                 style={{ width: `${Math.min(100, score)}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// MAIN PANEL COMPONENT
// ============================================================================

export const AIMasteringPanel = ({
  isVisible,
  onClose,
  projectId,
  projectName,
  audioCharacteristics,
  onApplySuggestion,
}) => {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  const [targetPlatform, setTargetPlatform] = useState('streaming');
  const [genre, setGenre] = useState('');
  const [userNotes, setUserNotes] = useState('');
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  
  // Analyze the master
  const analyzeMaster = useCallback(async () => {
    if (!audioCharacteristics) {
      toast.error('No audio characteristics available');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai-mastering/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId || 'default',
          project_name: projectName || 'Untitled Project',
          genre: genre || null,
          target_platform: targetPlatform,
          audio_characteristics: audioCharacteristics,
          user_notes: userNotes || null,
          previous_feedback: feedbackHistory.length > 0 ? feedbackHistory : null,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to analyze master');
      }
      
      const data = await response.json();
      setAnalysis(data);
      
      // Auto-expand first critical/high suggestion
      const firstImportant = data.suggestions?.find(s => 
        s.priority === 'critical' || s.priority === 'high'
      );
      if (firstImportant) {
        setExpandedSuggestion(firstImportant.id);
      }
      
    } catch (err) {
      console.error('Mastering analysis error:', err);
      setError(err.message);
      toast.error('Failed to analyze master');
    } finally {
      setIsLoading(false);
    }
  }, [audioCharacteristics, projectId, projectName, genre, targetPlatform, userNotes, feedbackHistory]);
  
  // Submit feedback
  const submitFeedback = useCallback(async (suggestionId, feedback) => {
    try {
      await fetch(`${API_URL}/api/ai-mastering/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: projectId || 'default',
          suggestion_id: suggestionId,
          rating: feedback.rating || 3,
          applied: feedback.applied || false,
          helpful: feedback.helpful,
          comment: feedback.comment || null,
        }),
      });
      
      // Store feedback locally for refinement
      setFeedbackHistory(prev => [...prev, { suggestionId, ...feedback }]);
      
    } catch (err) {
      console.error('Feedback error:', err);
    }
  }, [projectId]);
  
  // Handle suggestion apply
  const handleApplySuggestion = (suggestion) => {
    onApplySuggestion?.(suggestion);
    submitFeedback(suggestion.id, { applied: true, helpful: true, rating: 5 });
    toast.success(`Applied: ${suggestion.title}`);
  };
  
  // Auto-analyze when panel opens with characteristics
  useEffect(() => {
    if (isVisible && audioCharacteristics && !analysis && !isLoading) {
      analyzeMaster();
    }
  }, [isVisible, audioCharacteristics]);
  
  if (!isVisible) return null;
  
  // Group suggestions by category
  const groupedSuggestions = analysis?.suggestions?.reduce((acc, sug) => {
    const cat = sug.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(sug);
    return acc;
  }, {}) || {};
  
  const criticalCount = analysis?.suggestions?.filter(s => s.priority === 'critical').length || 0;
  const highCount = analysis?.suggestions?.filter(s => s.priority === 'high').length || 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      className="fixed top-4 right-4 bottom-4 w-[420px] bg-zinc-900/95 backdrop-blur-xl border border-zinc-700/50 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden"
      data-testid="ai-mastering-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-800 bg-gradient-to-r from-violet-500/10 to-rose-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-rose-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">AI Mastering</h2>
            <p className="text-xs text-zinc-400">Intelligent analysis & suggestions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
            className="w-8 h-8"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={analyzeMaster}
            disabled={isLoading}
            className="w-8 h-8"
          >
            <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} />
          </Button>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>
      </div>
      
      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-b border-zinc-800 overflow-hidden"
          >
            <div className="p-4 space-y-3">
              {/* Platform Selection */}
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Target Platform</label>
                <select
                  value={targetPlatform}
                  onChange={(e) => setTargetPlatform(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="mastering-platform-select"
                >
                  {PLATFORM_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Genre Selection */}
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Genre</label>
                <select
                  value={genre}
                  onChange={(e) => setGenre(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
                  data-testid="mastering-genre-select"
                >
                  <option value="">Not specified</option>
                  {GENRE_OPTIONS.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
              
              {/* User Notes */}
              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Notes for AI</label>
                <textarea
                  value={userNotes}
                  onChange={(e) => setUserNotes(e.target.value)}
                  placeholder="Any specific concerns or goals..."
                  rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 resize-none"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500/20 to-rose-500/20 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium">Analyzing your master...</p>
              <p className="text-xs text-zinc-400 mt-1">AI is examining audio characteristics</p>
            </div>
          </div>
        )}
        
        {/* Error State */}
        {error && !isLoading && (
          <div className="p-4 m-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Analysis Failed</span>
            </div>
            <p className="text-xs text-red-400/70 mt-1">{error}</p>
            <Button
              size="sm"
              onClick={analyzeMaster}
              className="mt-3 bg-red-500/20 hover:bg-red-500/30 text-red-400"
            >
              Try Again
            </Button>
          </div>
        )}
        
        {/* Results */}
        {analysis && !isLoading && (
          <div className="p-4 space-y-4">
            {/* Score Display */}
            <div className="p-4 bg-zinc-800/50 rounded-xl">
              <ScoreDisplay score={analysis.master_score} grade={analysis.grade} />
              <p className="text-sm text-zinc-300 mt-4">{analysis.summary}</p>
            </div>
            
            {/* Critical Issues Alert */}
            {analysis.critical_issues?.length > 0 && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">Critical Issues ({analysis.critical_issues.length})</span>
                </div>
                <ul className="space-y-1">
                  {analysis.critical_issues.map((issue, i) => (
                    <li key={i} className="text-xs text-red-300 flex items-start gap-2">
                      <span className="text-red-500 mt-0.5">•</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Quick Wins */}
            {analysis.quick_wins?.length > 0 && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <Zap className="w-4 h-4" />
                  <span className="text-sm font-medium">Quick Wins</span>
                </div>
                <ul className="space-y-1">
                  {analysis.quick_wins.map((win, i) => (
                    <li key={i} className="text-xs text-emerald-300 flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5">✓</span>
                      {win}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Suggestions */}
            {analysis.suggestions?.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium text-white">
                    Suggestions ({analysis.suggestions.length})
                  </h3>
                  <div className="flex items-center gap-2 text-xs">
                    {criticalCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">
                        {criticalCount} critical
                      </span>
                    )}
                    {highCount > 0 && (
                      <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                        {highCount} high
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {analysis.suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      suggestion={suggestion}
                      onApply={handleApplySuggestion}
                      onFeedback={submitFeedback}
                      isExpanded={expandedSuggestion === suggestion.id}
                      onToggle={() => setExpandedSuggestion(
                        expandedSuggestion === suggestion.id ? null : suggestion.id
                      )}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Genre & Platform Notes */}
            {(analysis.genre_notes || analysis.platform_notes) && (
              <div className="space-y-2">
                {analysis.genre_notes && (
                  <div className="p-3 bg-violet-500/10 border border-violet-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-violet-400 mb-1">
                      <Music className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Genre Notes</span>
                    </div>
                    <p className="text-xs text-violet-300">{analysis.genre_notes}</p>
                  </div>
                )}
                
                {analysis.platform_notes && (
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-cyan-400 mb-1">
                      <Target className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Platform Notes</span>
                    </div>
                    <p className="text-xs text-cyan-300">{analysis.platform_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Empty State */}
        {!analysis && !isLoading && !error && (
          <div className="flex flex-col items-center justify-center h-64 gap-4 p-4">
            <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
              <Brain className="w-8 h-8 text-zinc-600" />
            </div>
            <div className="text-center">
              <p className="text-zinc-400">No analysis yet</p>
              <p className="text-xs text-zinc-500 mt-1">
                {audioCharacteristics 
                  ? 'Click refresh to analyze your master'
                  : 'Play your project to gather audio characteristics'}
              </p>
            </div>
            {audioCharacteristics && (
              <Button onClick={analyzeMaster} className="bg-violet-500 hover:bg-violet-600">
                <Brain className="w-4 h-4 mr-2" />
                Analyze Master
              </Button>
            )}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/80">
        <div className="flex items-center justify-between text-[10px] text-zinc-500">
          <span>
            {analysis ? `Last analyzed: ${new Date(analysis.timestamp).toLocaleTimeString()}` : 'Not analyzed'}
          </span>
          <span>
            {feedbackHistory.length > 0 && `${feedbackHistory.length} feedback items`}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default AIMasteringPanel;
